import {
  MAX_TCG_IMPORT_ROWS,
  type TcgImportIssue,
  type TcgImportRow,
  tcgImportIssue,
} from "@loot-os/fulfillment-core/catalog/tcg-import";

/**
 * The TCG Asset Group Creator's CSV: one header row, then one card per line. Columns are
 * Asset Group Name, Product ID, Card Game and Card Printing. A blank line is skipped. A row
 * the creator would refuse is kept, with the reason, so the preview shows it before the import
 * runs.
 */

const COLUMNS = [
  { key: "name", header: "asset group name", label: "Asset Group Name" },
  { key: "productId", header: "product id", label: "Product ID" },
  { key: "cardGame", header: "card game", label: "Card Game" },
  { key: "printing", header: "card printing", label: "Card Printing" },
] as const;

type ColumnKey = (typeof COLUMNS)[number]["key"];

export interface CatalogBulkImportRow extends TcgImportRow {
  /** 1-based record number. It is the sheet line when no field contains a line break. */
  line: number;
  /** Set when this row cannot be created, including a product earlier in the same file. */
  issue: TcgImportIssue | null;
}

export interface ParsedCatalogBulkImport {
  rows: CatalogBulkImportRow[];
  /** Problems with the file itself. When one is set, `rows` is empty and nothing is imported. */
  fileIssues: string[];
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

/** RFC 4180 records. A quoted field may contain commas and line breaks. */
function parseCsvRecords(text: string): string[][] {
  const source = text.replace(/^\uFEFF/, "");
  const records: string[][] = [];
  let record: string[] = [];
  let cell = "";
  let quoted = false;

  for (let index = 0; index < source.length; index++) {
    const char = source[index];
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index++;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"' && cell.length === 0) {
      quoted = true;
      continue;
    }
    if (char === ",") {
      record.push(cell);
      cell = "";
      continue;
    }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && source[index + 1] === "\n") index++;
      record.push(cell);
      cell = "";
      records.push(record);
      record = [];
      continue;
    }
    cell += char ?? "";
  }

  if (cell.length > 0 || record.length > 0) {
    record.push(cell);
    records.push(record);
  }
  return records;
}

function isBlank(cells: string[]): boolean {
  return cells.every((cell) => cell.trim() === "");
}

function headerIndexes(cells: string[]): Map<ColumnKey, number> | null {
  const indexes = new Map<ColumnKey, number>();
  cells.forEach((cell, index) => {
    const header = normalizeHeader(cell);
    const column = COLUMNS.find((candidate) => candidate.header === header);
    if (column && !indexes.has(column.key)) indexes.set(column.key, index);
  });
  return indexes.size === COLUMNS.length ? indexes : null;
}

function cellAt(cells: string[], indexes: Map<ColumnKey, number>, key: ColumnKey): string {
  const index = indexes.get(key);
  return (index === undefined ? "" : (cells[index] ?? "")).trim();
}

function missingColumns(header: string[]): string {
  const missing = COLUMNS.filter(
    (column) => !header.some((cell) => normalizeHeader(cell) === column.header),
  );
  return missing.map((column) => column.label).join(", ");
}

/** Parses the creator's CSV. File-level problems leave `rows` empty. */
export function parseCatalogBulkImport(text: string): ParsedCatalogBulkImport {
  const records = parseCsvRecords(text);
  const headerAt = records.findIndex((record) => !isBlank(record));
  const header = headerAt === -1 ? undefined : records[headerAt];
  if (!header) return { rows: [], fileIssues: ["The file has no rows."] };

  const indexes = headerIndexes(header);
  if (!indexes) {
    return { rows: [], fileIssues: [`The file needs these columns: ${missingColumns(header)}.`] };
  }

  const rows: CatalogBulkImportRow[] = [];
  const seen = new Set<string>();
  let dataCount = 0;
  for (let offset = headerAt + 1; offset < records.length; offset++) {
    const cells = records[offset] ?? [];
    if (isBlank(cells)) continue;
    dataCount++;
    if (dataCount > MAX_TCG_IMPORT_ROWS) {
      return {
        rows: [],
        fileIssues: [`A bulk import holds at most ${MAX_TCG_IMPORT_ROWS} rows.`],
      };
    }
    const row: TcgImportRow = {
      name: cellAt(cells, indexes, "name"),
      productId: cellAt(cells, indexes, "productId"),
      cardGame: cellAt(cells, indexes, "cardGame"),
      printing: cellAt(cells, indexes, "printing"),
    };
    let issue = tcgImportIssue(row);
    if (!issue) {
      const key = `${row.productId}\0${row.printing}`;
      if (seen.has(key)) issue = "duplicate_filter";
      else seen.add(key);
    }
    rows.push({ ...row, line: offset + 1, issue });
  }

  if (rows.length === 0) return { rows: [], fileIssues: ["The file has no rows."] };
  return { rows, fileIssues: [] };
}
